import { PaymentMethodType, toBool } from 'vopay';
import { useClientData } from '@/providers/ClientData.provider';
import { getPaymentSelectionMethod } from './useHandlePaymentMethodChange';

/**
 * Which payment methods can move the currency a Collect or Pay form is set to.
 *
 * In the account's own currency (CAD for CA, USD for everyone else) every rail applies as before.
 * The other currency — USD on a Canadian account, CAD on a US one — is reachable only through
 * specific rails, mirroring earthnode's `VIOElinxCurrencyHelper`:
 *
 * - Card collection is per currency because the processor's credentials are: the login response
 *   reports `CADCreditCardCollectionEnabled` / `USDCreditCardCollectionEnabled` separately.
 * - Bank is USD EFT on a Canadian account — `USDEFTCollectEnabled` to collect, `USDEFTSendEnabled`
 *   to pay — and nothing on a US account.
 * - Interac settles in CAD only. The wallets' processor credentials are per currency with no flag
 *   to read, so they are not offered.
 * - The card pulls and pushes (AFT / OCT) take a Currency, so debit stays on offer for a Canadian
 *   account in USD when the method is provided; PayPal recipients carry their own currency.
 *
 * An eLinx *request* in the other currency is stricter: earthnode narrows the hosted page to card
 * (collect) and bank, and rejects anything else at acceptance — pass `isRequested` for those
 * pickers so the portal never offers a method the payer could not use.
 *
 * This hook only ever answers "can this rail move this currency". Whether the account is entitled
 * to a rail at all is the locked-features provider's job — an un-entitled method stays in the list
 * so it renders locked with its upsell, rather than disappearing.
 */
export default function usePaymentMethodCurrencySupport(formType: 'Pay' | 'Collect' | 'Connect' | 'Flex') {
  const {
    response: {
      Country,
      USDEFTCollectEnabled,
      USDEFTSendEnabled,
      CreditCardEnabled,
      CADCreditCardCollectionEnabled,
      USDCreditCardCollectionEnabled,
    },
  } = useClientData();

  const domesticCurrency = Country === 'CA' ? 'CAD' : 'USD';

  /** Whether the form is in the account's own currency — the only case an eLinx may bring its own iframe. */
  function isDomesticCurrency(currency: string | undefined): boolean {
    return (currency || domesticCurrency) === domesticCurrency;
  }

  // A backend older than earthnode ID-13651 omits both per-currency flags and they normalize to
  // false, which is indistinguishable from "configured for neither currency". Treat a card-enabled
  // account that reports no currency at all as the pre-ID-13651 shape and fall back to the old
  // all-currencies behaviour: briefly offering a card an account cannot charge (the processor
  // rejects it, with a message) beats hiding a rail that works today from every account during a
  // deploy. Remove this once ID-13651 is everywhere.
  const hasPerCurrencyCardFlags = CADCreditCardCollectionEnabled || USDCreditCardCollectionEnabled;

  function isCardCollectionEnabled(currency: string): boolean {
    if (!toBool(CreditCardEnabled) || !hasPerCurrencyCardFlags) return true;
    if (currency === 'USD') return USDCreditCardCollectionEnabled;
    if (currency === 'CAD') return CADCreditCardCollectionEnabled;

    return true;
  }

  /** Currencies the account can collect in at all, before a rail narrows them further. */
  function isCollectCurrencyEnabled(currency: 'CAD' | 'USD'): boolean {
    if (currency === domesticCurrency) return true;
    if (currency === 'USD') return toBool(USDEFTCollectEnabled) || USDCreditCardCollectionEnabled;

    return CADCreditCardCollectionEnabled;
  }

  function isSelectionMethodAvailable(
    selectionMethod: string,
    currency: string | undefined,
    { isRequested = false }: { isRequested?: boolean } = {},
  ): boolean {
    // Connect and Flex have no currency picker, so nothing to narrow.
    if (formType !== 'Collect' && formType !== 'Pay') return true;

    const formCurrency = currency || domesticCurrency;

    if (formType === 'Collect' && selectionMethod === 'credit') return isCardCollectionEnabled(formCurrency);
    if (formCurrency === domesticCurrency) return true;

    // The account's other currency. Credit on a collection was settled above; a US account has
    // no other rail here, so from this point only a Canadian account in USD can match.
    const isUSDOnCanadianAccount = Country === 'CA' && formCurrency === 'USD';

    if (selectionMethod === 'bank') {
      return isUSDOnCanadianAccount && toBool(formType === 'Pay' ? USDEFTSendEnabled : USDEFTCollectEnabled);
    }

    if (isRequested) return false;

    if (selectionMethod === 'debitcard') return isUSDOnCanadianAccount;
    if (selectionMethod === 'paypal') return formType === 'Pay';

    return false;
  }

  function isPaymentMethodAvailable(paymentMethod: PaymentMethodType, currency: string | undefined): boolean {
    return isSelectionMethodAvailable(getPaymentSelectionMethod(paymentMethod), currency);
  }

  return { isDomesticCurrency, isCollectCurrencyEnabled, isSelectionMethodAvailable, isPaymentMethodAvailable };
}
